import classNames from 'classnames';
import React, { useLayoutEffect } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import useLatestConversationList from '../../../hooks/conversation/useLatestConversationList';
import useWindowWidth from '../../../hooks/useWindowWidth';
import { lastItem } from '../../../utils/arrayUtil';
import BackLink from '../../ibok-core/module/BackLink';
import ConversationList from '../ConversationList';
import ServiceUnavailableMessage from './ServiceUnavailableMessage';
import useBrowserAuthMe from '../../../hooks/useBrowserAuthMe';
import './index.less';
export var isHttpError = function (error) { return !!error.data && !!error.data.status; };
var isMobile = function (width) { return width <= 690; };
var MyConversationsTitle = function () { return React.createElement("h1", { className: "pagetitle" }, "Mine Samtaler"); };
var isMessageView = function (location) {
    var pathname = location.pathname;
    var segments = pathname
        .split('/')
        // Remove any empty segments, that may be caused by leading or trailing slashes
        .filter(function (segment) { return segment.length > 0; });
    var lastSegment = lastItem(segments);
    return lastSegment && lastSegment !== 'chat';
};
var NoConversationFeedback = function () { return React.createElement("section", { className: "noconversationfeedback" },
    React.createElement("h3", null, "Finner ingen samtaler"),
    React.createElement("p", null, "Det ser ikke ut som at du har noen samtaler tilgjengelig. N\u00E5r du tar kontakt med en selger eller en kj\u00F8per tar kontakt med deg, vil samtalen dukke opp her.")); };
var MobileNavHeader = function () { return React.createElement("nav", { className: "conversationnavheader" },
    React.createElement(BackLink, { to: "/chat/" }, "Samtaler")); };
var isServiceUnavailable = function (res) {
    if (!res || !res.status) {
        return false;
    }
    return res.status === 503;
};
/**
 * ChatLayout is the outer layer of the chat-pages.
 * Its responsible for structuring the pages to be usable in both mobile and desktop.
 *
 * - Desktop will use a master-detail view, where the list of conversations are to the left
 *   and a selected conversation is to the right
 * - Mobile will first be presented by the list of conversations, and when clicked on be
 *   navigated to the messages of that conversation.
 *
 * Its also responsible for retrieving a list of conversations and catch global errors.
 * */
var ChatLayout = function (_a) {
    var children = _a.children;
    var match = useRouteMatch();
    var location = useLocation();
    var currentUser = useBrowserAuthMe().user;
    var _b = useLatestConversationList(false), error = _b.error, isLoading = _b.isLoading, conversations = _b.conversations;
    var currentWidth = useWindowWidth();
    var shouldShowMessage = !isMobile(currentWidth) || isMessageView(location);
    var shouldShowInDesktopOrMessage = !isMobile(currentWidth) || !isMessageView(location);
    var isConversationView = !!isMessageView(location);
    useLayoutEffect(function () {
        var isMobileConversation = isMobile(currentWidth) && isConversationView;
        var viewport = window.visualViewport;
        var updateChatViewport = function () {
            var _a, _b;
            document.body.classList.toggle('chat-message-view', isMobileConversation);
            if (!isMobileConversation) {
                return;
            }
            var viewportHeight = (_a = viewport === null || viewport === void 0 ? void 0 : viewport.height) !== null && _a !== void 0 ? _a : window.innerHeight;
            if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) {
                return;
            }
            var reportedTop = (_b = viewport === null || viewport === void 0 ? void 0 : viewport.offsetTop) !== null && _b !== void 0 ? _b : 0;
            var viewportTop = Number.isFinite(reportedTop) ? reportedTop : 0;
            document.documentElement.style.setProperty('--chat-visual-viewport-height', viewportHeight + "px");
            document.documentElement.style.setProperty('--chat-visual-viewport-top', viewportTop + "px");
        };
        updateChatViewport();
        window.addEventListener('resize', updateChatViewport);
        viewport === null || viewport === void 0 ? void 0 : viewport.addEventListener('resize', updateChatViewport);
        viewport === null || viewport === void 0 ? void 0 : viewport.addEventListener('scroll', updateChatViewport);
        return function () {
            window.removeEventListener('resize', updateChatViewport);
            viewport === null || viewport === void 0 ? void 0 : viewport.removeEventListener('resize', updateChatViewport);
            viewport === null || viewport === void 0 ? void 0 : viewport.removeEventListener('scroll', updateChatViewport);
            document.body.classList.remove('chat-message-view');
            document.documentElement.style.removeProperty('--chat-visual-viewport-height');
            document.documentElement.style.removeProperty('--chat-visual-viewport-top');
        };
    }, [currentWidth, isConversationView]);
    var currentConversationSlug = match.params.slug;
    // We must take into account that the service might be down
    if (error && isHttpError(error) && isServiceUnavailable(error.response)) {
        return React.createElement(React.Fragment, null,
            React.createElement(MyConversationsTitle, null),
            React.createElement(ServiceUnavailableMessage, null));
    }
    // eslint-disable-next-line no-undef
    var conversationContent = null;
    if (!currentUser) {
        conversationContent = null;
    }
    else if (conversations.length > 0 || isLoading) {
        conversationContent = React.createElement(ConversationList, { selectedConversationSlug: currentConversationSlug, conversations: conversations, currentUser: currentUser });
    }
    else {
        conversationContent = React.createElement(NoConversationFeedback, null);
    }
    return React.createElement(React.Fragment, null,
        shouldShowInDesktopOrMessage && React.createElement(MyConversationsTitle, null),
        React.createElement("div", { className: "chatlayout" },
            shouldShowInDesktopOrMessage && React.createElement("aside", { className: "chatlayout__conversations" }, conversationContent),
            isMobile(currentWidth) && shouldShowMessage && React.createElement(MobileNavHeader, null),
            shouldShowMessage && React.createElement("article", { className: classNames('chatlayout__messagebox', {
                    'classchatlayout__messagebox--mobile': !isMobile(currentWidth),
                }) }, children)));
};
export default ChatLayout;
